import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import {
  FiHome, FiPlusCircle, FiCoffee, FiClock, FiZap,
  FiDownload, FiMessageSquare, FiShield, FiLogOut,
  FiBarChart2, FiChevronDown, FiSettings,
} from 'react-icons/fi';

const navItems = [
  { path: '/', label: 'Dashboard', icon: FiHome },
  {
    label: 'Check-in',
    icon: FiPlusCircle,
    children: [
      { path: '/checkin', label: 'Daily Check-in', icon: FiPlusCircle },
      { path: '/meals', label: 'Meals', icon: FiCoffee },
      { path: '/recovery', label: 'Recovery', icon: FiShield },
    ],
  },
  {
    label: 'Tracking',
    icon: FiBarChart2,
    children: [
      { path: '/history', label: 'History', icon: FiClock },
      { path: '/insights', label: 'Insights', icon: FiZap },
      { path: '/export', label: 'Export', icon: FiDownload },
    ],
  },
  { path: '/chat', label: 'Chat', icon: FiMessageSquare },
];

function NavDropdown({ item, isOpen, onToggle, onClose, location }) {
  const ref = useRef(null);
  const isActive = item.children.some(
    (child) => location.pathname === child.path ||
               (child.path !== '/' && location.pathname.startsWith(child.path))
  );

  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    }
    function handleEscape(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const Icon = item.icon;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={onToggle}
        className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition
          ${isActive
            ? 'bg-white/30 text-white'
            : 'text-white/70 hover:bg-white/20 hover:text-white'
          }`}
      >
        <Icon size={16} />
        <span className="hidden sm:inline">{item.label}</span>
        <FiChevronDown
          size={12}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 glass rounded-lg py-1 min-w-[170px] z-50 shadow-lg">
          {item.children.map(({ path, label, icon: ChildIcon }) => {
            const childActive = location.pathname === path ||
              (path !== '/' && location.pathname.startsWith(path));
            return (
              <Link
                key={path}
                to={path}
                onClick={onClose}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition
                  ${childActive
                    ? 'bg-white/25 text-white'
                    : 'text-white/80 hover:bg-white/15 hover:text-white'
                  }`}
              >
                <ChildIcon size={15} />
                {label}
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function NavBar() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [openDropdown, setOpenDropdown] = useState(null);

  // Close dropdowns on route change
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  return (
    <nav className="glass-nav sticky top-0 z-10">
      <div className="max-w-4xl lg:max-w-6xl mx-auto px-4 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="text-xl font-bold text-white drop-shadow-sm">
            EndoTrack
          </Link>

          <div className="flex items-center gap-1">
            {navItems.map((item, idx) => {
              if (item.children) {
                return (
                  <NavDropdown
                    key={item.label}
                    item={item}
                    isOpen={openDropdown === idx}
                    onToggle={() => setOpenDropdown(openDropdown === idx ? null : idx)}
                    onClose={() => setOpenDropdown(null)}
                    location={location}
                  />
                );
              }

              const Icon = item.icon;
              const isActive = item.path === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(item.path);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition
                    ${isActive
                      ? 'bg-white/30 text-white'
                      : 'text-white/70 hover:bg-white/20 hover:text-white'
                    }`}
                >
                  <Icon size={16} />
                  <span className="hidden sm:inline">{item.label}</span>
                </Link>
              );
            })}

            <div className="ml-3 pl-3 border-l border-white/30 flex items-center gap-2">
              <span className="text-sm text-white/70 hidden sm:inline">{user?.name}</span>
              <Link
                to="/settings"
                className={`p-2 transition ${
                  location.pathname === '/settings'
                    ? 'text-white'
                    : 'text-white/60 hover:text-white'
                }`}
                title="Settings"
              >
                <FiSettings size={16} />
              </Link>
              <button
                onClick={logout}
                className="text-white/60 hover:text-white p-2"
                title="Logout"
              >
                <FiLogOut size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
